import React, { useState, useMemo } from 'react'
import { useGetInventory } from '../../../../api/dealership/inventory/useGetInventory'
import Grid from '../../../../components/Grid/Grid'
import GridItemVehicle from '../../../../components/Grid/Items/GridItemVehicle'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import TextInput from '../../../../components/FormElements/TextInput'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { Vehicle } from '../../../../lib/types/Vehicle'
import { DropdownButton, RadioInput, Table } from '@demandlocal/components'
import SortingController from '../../../../components/Sorting/SortingController'
import { ReactComponent as IconChangeViewCard } from '../../../../images/icons/icon_change_view_card.svg'
import { ReactComponent as IconChangeViewList } from '../../../../images/icons/icon_change_view_list.svg'
import { createColumnHelper } from '@tanstack/react-table'
import { useVehiclesListView } from '../../../../store/zustand/vehiclesListStore'
import { motion } from 'framer-motion'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { useGetInventoryDataProviderDetails } from '../../../../api/dealership/inventory/useGetInventoryDataProviderDetails'
import { uniq } from 'lodash'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import GoogleSheetsExport from '../../../../forms/Exports/GoogleSheetsExport'
import { useDealership } from '../../../../store/zustand/dealershipStore'

const columnHelper = createColumnHelper<Vehicle>()

const VehiclesList = (): JSX.Element => {
  const { view, setView } = useVehiclesListView()
  const [filteredTotal, setFilteredTotal] = useState<number | undefined>(undefined)
  const [filters, setFilters] = useState<Partial<Vehicle>>({})
  const [sorting, setSorting] = useState<Record<string, unknown>>({})
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const sortingPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [exportGoogleSheet, setExportGoogleSheet] = useState<boolean>(false)
  const dealership = useDealership()

  const { data: inventoryDataProviders, isLoading: isLoadingDataProviders } =
    useGetInventoryDataProviderDetails()

  const inventoryColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'vehicle',
        header: 'Vehicle',
        cell: (info) => (
          <Link
            className="font-bold text-primary-persian-blue cursor-pointer"
            to={`../vehicles/${info.row.original.vin_number}`}>
            {info.row.original.make} {info.row.original.model}
          </Link>
        ),
      }),
      columnHelper.accessor('vehicle_state', {
        header: 'Vehicle State',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('stock_number', {
        header: 'Stock Number',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('year', {
        header: 'Year',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('make', {
        header: 'Make',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('model', {
        header: 'Model',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('raw_exterior_color', {
        header: 'Color',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('raw_body', {
        header: 'Body',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => formatCurrency(Number(info.getValue())),
      }),
      columnHelper.accessor('msrp', {
        header: 'MSRP',
        cell: (info) => formatCurrency(Number(info.getValue())),
      }),
      columnHelper.accessor('mileage', {
        header: 'Mileage',
        cell: (info) => formatNumber(Number(info.getValue())),
      }),
      columnHelper.accessor('raw_trim', {
        header: 'Trim',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('raw_vehicle_certification', {
        header: 'Certified',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('days_on_lot', {
        header: 'Days on Lot',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('last_seen', {
        header: 'Last Updated',
        cell: (info) => dayjs(info.getValue()).format('YYYY-MM-DD'),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('raw_vehicle_destination_url', {
        header: 'Destination URL',
        cell: (info) => (
          <div className="max-w-[100px] truncate">
            {info.getValue().length > 0 && (
              <a
                className="font-bold text-primary-persian-blue"
                href={info.getValue()}
                target="_blank"
                rel="noreferrer">
                {info.getValue()}
              </a>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('location', {
        header: 'Location',
        cell: (info) => info.getValue(),
      }),
    ],
    [],
  )

  const displayFilterCount = useMemo(() => {
    return Boolean(
      filteredTotal &&
        filteredTotal > 0 &&
        Object.values(filters).filter((f) => !!f).length > 0,
    )
  }, [filteredTotal, filters])

  return (
    <div className={'flex flex-col flex-1 pb-8'}>
      <div className="text-md rounded-xl p-2 bg-gainsboro-gray/40 border-2 mb-4">
        View the daily inventory feed received from your inventory provider.
      </div>
      {!isLoadingDataProviders &&
        inventoryDataProviders?.data &&
        inventoryDataProviders.data.length > 0 && (
          <span className="font-bold text-s pb-2">
            Imported from:{' '}
            {uniq(
              inventoryDataProviders?.data?.map((d) =>
                d.from_website ? 'Dealer Site' : d.inventory_data_provider_name,
              ),
            ).join(', ')}
          </span>
        )}
      <div className={'pb-4 flex flex-row justify-between'}>
        <div className={'flex flex-1 flex-row justify-between gap-4'}>
          <OutPortal node={sortingPortalNode} />
          <div className="flex flex-row gap-1 items-center">
            {displayFilterCount && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                key={'filters-total'}
                className={
                  'h-5 bg-primary-persian-blue text-center flex items-center text-white p-1.5 rounded-md text-xs font-bold'
                }>
                <span>{filteredTotal} Vehicles</span>
              </motion.div>
            )}
            <div>
              <OutPortal node={filtersPortalNode} />
            </div>
            <DropdownButton
              onClick={(e) => setExportGoogleSheet(false)}
              options={[
                {
                  label: 'Google sheet',
                  onClick: (e) => setExportGoogleSheet(true),
                },
              ]}>
              Export
            </DropdownButton>
            <div className={'flex items-center gap-3'}>
              {view === 'grid' && (
                <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                  <IconChangeViewList
                    className={
                      'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                    }
                  />
                </div>
              )}
              {view === 'table' && (
                <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                  <IconChangeViewCard
                    className={'cursor-pointer stroke-primary-xiketic'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {exportGoogleSheet && (
        <GoogleSheetsExport
          setExportGoogleSheet={setExportGoogleSheet}
          apiEndpoint={`/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/inventory/list`}
          filters={filters}
          columns={inventoryColumns}
        />
      )}
      {view === 'grid' && (
        <SortingController
          portalNode={sortingPortalNode}
          setSorting={setSorting}
          choices={[
            { label: 'Price', key: 'price' },
            { label: 'Mileage', key: 'mileage' },
            { label: 'Year', key: 'year' },
          ]}
        />
      )}
      <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
        <TextInput name={'raw_make'} placeholder={'Make'} label={'Make'} />
        <TextInput name={'raw_model'} placeholder={'Model'} label={'Model'} />
        <TextInput name={'raw_body'} placeholder={'Body'} label={'Body'} />
        <TextInput name={'stock_number'} placeholder={'Stock'} label={'Stock number'} />
        <TextInput name={'vin_number'} placeholder={'VIN'} label={'VIN'} />
        <TextInput name={'year'} placeholder={'Year'} label={'Year'} />
        <TextInput
          type="number"
          name={'min_days_on_lot'}
          placeholder={'0'}
          label={'Minimum Days on Lot'}
        />
        <TextInput
          type="number"
          name={'max_days_on_lot'}
          placeholder={'90'}
          label={'Maximum Days on Lot'}
        />
        <RadioInput
          name={'vehicle_state'}
          options={[
            { label: 'New', value: 'New' },
            { label: 'Used', value: 'Used' },
          ]}
          label={'Vehicle State'}
        />
      </FiltersContext>
      {view === 'grid' && (
        <Grid
          fetchFn={useGetInventory}
          gridComponent={GridItemVehicle}
          filters={filters}
          sorting={sorting as Record<string, string | undefined>}
          setTotalRows={(v) => setFilteredTotal(v)}
        />
      )}
      {view === 'table' && (
        <Table
          isDownloadable
          emptyLabel={'No vehicles found'}
          columns={inventoryColumns}
          filters={{ ...filters }}
          fetchFn={useGetInventory}
          setTotalRows={(v) => setFilteredTotal(v)}
        />
      )}
    </div>
  )
}

export default VehiclesList
